/* eslint-disable react/no-danger */
import React from 'react';
import { navigate } from 'gatsby';
import {
  mdiAccountCheckOutline,
  mdiAccountCircleOutline,
  mdiAirplane,
  mdiCalendarClockOutline,
  mdiCancel,
  mdiCartOutline,
  mdiCashFast,
  mdiCashMultiple,
  mdiCashRemove,
  mdiCellphone,
  mdiCellphoneLink,
  mdiChartTimelineVariantShimmer,
  mdiCheck,
  mdiClipboardTextOutline,
  mdiClockCheckOutline,
  mdiCreditCardOutline,
  mdiCurrencyUsdOff,
  mdiFileDocumentOutline,
  mdiHandCoinOutline,
  mdiHomeOutline,
  mdiLightbulbOnOutline,
  mdiMessageAlertOutline,
  mdiMonitorCellphoneStar,
  mdiThumbUpOutline,
  mdiTimerCheckOutline,
  mdiTrophyVariantOutline
} from '@mdi/js';
import { Icon, Margin } from '@upgrade/react-components';
import useWindowMinWidth from '@upgrade/react-components/hooks/useWindowMinWidth';
import {
  StyledInfoContainer,
  StyledList,
  StyledInfoLink,
  StyledImage,
  StyledBorder,
  StyledTextItem,
  StyledHeadingContainer
} from './styles';
import { toHtmlSanitize } from '../../../utils/remark-html';
import { StyledBody } from '../typography';
import { Heading, Text } from '../TypographyPublicSite';
import Image from '../Image';

const SupportedIcons = {
  'account-check-outline': mdiAccountCheckOutline,
  'account-circle-outline': mdiAccountCircleOutline,
  airplane: mdiAirplane,
  'calendar-clock-outline': mdiCalendarClockOutline,
  cancel: mdiCancel,
  'cart-outline': mdiCartOutline,
  'cash-fast': mdiCashFast,
  'cash-multiple': mdiCashMultiple,
  'cash-remove': mdiCashRemove,
  cellphone: mdiCellphone,
  'cellphone-link': mdiCellphoneLink,
  'chart-timeline-variant-shimmer': mdiChartTimelineVariantShimmer,
  check: mdiCheck,
  'clock-check-outline': mdiClockCheckOutline,
  'credit-card-outline': mdiCreditCardOutline,
  'currency-usd-off': mdiCurrencyUsdOff,
  'file-document-outline': mdiFileDocumentOutline,
  'hand-coin-outline': mdiHandCoinOutline,
  'home-outline': mdiHomeOutline,
  'lightbulb-on-outline': mdiLightbulbOnOutline,
  'message-alert-outline': mdiMessageAlertOutline,
  'monitor-cellphone-star': mdiMonitorCellphoneStar,
  'timer-check-outline': mdiTimerCheckOutline,
  'trophy-variant-outline': mdiTrophyVariantOutline,
  'clipboard-text-outline': mdiClipboardTextOutline,
  'thumb-up-outline': mdiThumbUpOutline
};

const getIcon = iconName => SupportedIcons[iconName];

const InfoContainer = ({
  icon,
  iconColor,
  iconTitle,
  image,
  imageWidth,
  imageHeight,
  imageMaxHeight,
  imagePosition = 'top',
  titleColor,
  fontColor,
  title,
  description,
  list,
  link,
  ctaTitle,
  addTitleBorder,
  descriptionFontWeight,
  styledText,
  styledTextAlt,
  topBorder,
  alignItems = 'flex-start',
  showTrustpilotSection = false,
  snippet,
  xsAlignItems,
  xsImagePosition,
  xsImageHeight,
  xsImageWidth
}) => {
  const [isSm] = useWindowMinWidth('sm');
  const resolvedAlignItems = isSm || !xsAlignItems ? alignItems : xsAlignItems;
  const resolvedImagePosition = isSm || !xsImagePosition ? imagePosition : xsImagePosition;
  const resolvedImageHeight = isSm || !xsImageHeight ? imageHeight : xsImageHeight;
  const resolvedImageWidth = isSm || !xsImageWidth ? imageWidth : xsImageWidth;
  return (
    <StyledInfoContainer
      {...{ topBorder }}
      alignItems={resolvedAlignItems}
      isHorizontalSteps={!!styledText}
      $imagePosition={resolvedImagePosition}
    >
      {image && (
        <StyledImage
          src={image}
          width={resolvedImageWidth}
          height={resolvedImageHeight}
          {...{ imageMaxHeight, centerImage: resolvedAlignItems === 'center' }}
        />
      )}
      <div>
        {styledText && (
          <Heading as="h2" color={titleColor}>
            {styledText}
          </Heading>
        )}
        {styledTextAlt && (
          <StyledTextItem {...{ topBorder, titleFontWeight: 400 }} data-color={fontColor}>
            {styledTextAlt}
          </StyledTextItem>
        )}
        {(title || icon) && (
          <StyledHeadingContainer $alignItems={resolvedAlignItems}>
            {icon && (
              <>
                <Icon
                  className="hidden visible-sm"
                  icon={getIcon(icon)}
                  color={iconColor || 'accentPrimary2'}
                  size="32"
                  title={iconTitle}
                  aria-hidden
                />
                <Icon
                  className="hidden-sm inline-icon"
                  icon={getIcon(icon)}
                  color={iconColor || 'accentPrimary2'}
                  size="24"
                  title={iconTitle}
                  aria-hidden
                />
              </>
            )}
            {title && (
              <Heading
                as="h3"
                color={titleColor}
                fontWeight={400}
                marginBottom="none"
                size="xs"
                dangerouslySetInnerHTML={{ __html: toHtmlSanitize(title) }}
              />
            )}
          </StyledHeadingContainer>
        )}
        {addTitleBorder && <StyledBorder data-color={fontColor} />}
        {showTrustpilotSection && (
          <Margin bottom="md">
            <Image src="/img/trustpilot-new-logo.png" width="200" height="21" />
          </Margin>
        )}
        {snippet && (
          <div
            dangerouslySetInnerHTML={{
              __html: snippet
            }}
          />
        )}
        {description && (
          <Text
            align={resolvedAlignItems === 'center' ? 'center' : 'left'}
            color={fontColor}
            marginBottom="sm"
            size="md"
            weight={descriptionFontWeight}
            dangerouslySetInnerHTML={{ __html: toHtmlSanitize(description, { removeWrappingPTag: false }) }}
          />
        )}
        {list && (
          <StyledList>
            {list.map((e, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`item-${i}`}>
                <StyledBody>{e}</StyledBody>
              </li>
            ))}
          </StyledList>
        )}
        {link && ctaTitle && (
          <StyledInfoLink emphasis="secondary" onClick={() => navigate(link)}>
            {ctaTitle}
          </StyledInfoLink>
        )}
      </div>
    </StyledInfoContainer>
  );
};

export default InfoContainer;
